import type { SectionProps } from "../context";
import { field } from "../context";
import { CARD_HOVER, Container, Img, MultiLine, OrderLink, Price, REVEAL, STACK, STAGGER, SectionHeader, SectionShell, cx } from "../ui";

const ASK = "Halo {{nama_usaha}}, saya mau tanya soal {{produk}}.";
const askLink =
  "inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-site-primary underline-offset-4 hover:underline";

export function Services({ node, ctx }: SectionProps<"services">) {
  const c = node.content;
  const header = <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} />;

  if (node.variant === "list") {
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container>
          <div className="grid gap-10 @4xl:grid-cols-[1fr_1.6fr] @4xl:gap-16">
            <div className="@4xl:sticky @4xl:top-[calc(7rem+var(--baze-sticky-top,0px))] @4xl:self-start">{header}</div>
            <ul className="divide-y divide-site-border border-y border-site-border">
              {c.items.map((item, i) => (
                <li key={i} className="baze-reveal grid gap-4 py-7 @xl:grid-cols-[1fr_auto] @xl:items-start @xl:gap-8">
                  <div className="flex gap-5">
                    {item.image ? (
                      <div className="size-20 shrink-0 overflow-hidden rounded-site-md bg-site-surface">
                        <Img ctx={ctx} image={item.image} sizes="80px" target={480} path={`content.items.${i}.image`} />
                      </div>
                    ) : null}
                    <div>
                      <h3 className="baze-heading text-xl leading-snug @3xl:text-2xl" {...field(ctx, `content.items.${i}.title`)}>
                        {item.title}
                      </h3>
                      <MultiLine ctx={ctx} text={item.description} path={`content.items.${i}.description`} className="mt-2 leading-relaxed text-site-muted" />
                    </div>
                  </div>
                  <div className="flex flex-row items-center justify-between gap-4 @xl:flex-col @xl:items-end @xl:text-right">
                    <Price value={item.price} note={item.priceNote} className="text-lg text-site-text @xl:justify-end" />
                    <OrderLink ctx={ctx} message={ASK} produk={item.title} label="Tanya" className={askLink} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </SectionShell>
    );
  }

  if (node.variant === "index") {
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container size="wide">
          {header}
          <ol className={cx(STACK, STAGGER, "border-t-2 border-site-text")}>
            {c.items.map((item, i) => (
              <li key={i} className="group relative border-b border-site-border">
                <div className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 py-7 transition-[padding] duration-300 motion-reduce:transition-none @4xl:group-hover:pl-4 @3xl:gap-x-10 @3xl:py-9 @4xl:grid-cols-[6rem_1.1fr_1.4fr_auto] @4xl:items-baseline">
                  <span aria-hidden="true" className="baze-heading text-4xl leading-none text-site-primary tabular-nums @3xl:text-6xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="baze-heading self-center text-2xl leading-tight @3xl:text-[2rem] @4xl:self-baseline" {...field(ctx, `content.items.${i}.title`)}>
                    {item.title}
                  </h3>
                  <div className="col-start-2 @4xl:col-start-auto">
                    {item.image ? (
                      <div className={cx("mb-4 aspect-[16/9] w-full max-w-xs overflow-hidden rounded-site-sm bg-site-surface", !ctx.editor && "@4xl:hidden")}>
                        <Img ctx={ctx} image={item.image} sizes="320px" target={640} path={`content.items.${i}.image`} />
                      </div>
                    ) : null}
                    <MultiLine ctx={ctx} text={item.description} path={`content.items.${i}.description`} className="leading-relaxed text-site-muted" />
                  </div>
                  <div className="col-start-2 flex items-center gap-5 @4xl:col-start-auto @4xl:flex-col @4xl:items-end @4xl:text-right">
                    <Price value={item.price} note={item.priceNote} className="text-lg text-site-text @4xl:justify-end" />
                    <OrderLink ctx={ctx} message={ASK} produk={item.title} label="Tanya" className={askLink} />
                  </div>
                </div>
                {item.image && !ctx.editor ? (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute top-1/2 right-[18%] z-10 hidden aspect-[4/3] w-56 -translate-y-1/2 scale-90 rotate-3 overflow-hidden rounded-site-md opacity-0 shadow-site-lg transition duration-300 group-hover:scale-100 group-hover:opacity-100 motion-reduce:transition-none @4xl:block"
                  >
                    <Img ctx={ctx} image={item.image} sizes="224px" target={480} />
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
        </Container>
      </SectionShell>
    );
  }

  if (node.variant === "alternating") {
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container>
          <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} align="center" />
          <ul className={cx(STACK, "space-y-14 @3xl:space-y-24")}>
            {c.items.map((item, i) => {
              const flip = i % 2 === 1;
              return (
                <li key={i} className={cx("grid items-center gap-8 @3xl:gap-14", item.image && "@3xl:grid-cols-2")}>
                  {item.image ? (
                    <div className={cx("group relative", flip ? "baze-reveal-left @3xl:order-2" : "baze-reveal-right")}>
                      <div aria-hidden="true" className={cx("absolute -inset-3 -z-0 rounded-site-lg bg-site-secondary/20", flip ? "-rotate-2" : "rotate-2")} />
                      <div className="relative aspect-[4/3] overflow-hidden rounded-site-lg bg-site-surface shadow-site-md">
                        <Img
                          ctx={ctx}
                          image={item.image}
                          sizes="(min-width: 768px) 50vw, 100vw"
                          path={`content.items.${i}.image`}
                          className="transition-transform duration-700 group-hover:scale-[1.04] motion-reduce:transition-none"
                        />
                      </div>
                    </div>
                  ) : null}
                  <div className={cx(REVEAL, !item.image && "mx-auto max-w-2xl text-center")}>
                    <span aria-hidden="true" className="baze-heading block text-5xl leading-none text-site-primary/25 tabular-nums @3xl:text-6xl">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="baze-heading mt-3 text-2xl leading-snug @3xl:text-[2rem]" {...field(ctx, `content.items.${i}.title`)}>
                      {item.title}
                    </h3>
                    <MultiLine ctx={ctx} text={item.description} path={`content.items.${i}.description`} className="mt-3 text-base leading-relaxed text-site-muted @3xl:text-lg" />
                    {item.price !== undefined || item.priceNote || ctx.site.contact.whatsapp ? (
                      <div className={cx("mt-6 flex flex-wrap items-center gap-x-6 gap-y-3", !item.image && "justify-center")}>
                        <Price value={item.price} note={item.priceNote} className="text-lg text-site-text" />
                        <OrderLink ctx={ctx} message={ASK} produk={item.title} label="Tanya" className={askLink} />
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </Container>
      </SectionShell>
    );
  }

  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        {header}
        <ul className={cx(STACK, STAGGER, "grid gap-5 @2xl:grid-cols-2 @4xl:grid-cols-3 @3xl:gap-6")}>
          {c.items.map((item, i) => (
            <li key={i} className={cx("group flex flex-col overflow-hidden rounded-site-md border border-site-border bg-site-surface shadow-site-sm hover:border-site-primary/30", CARD_HOVER)}>
              {item.image ? (
                <div className="aspect-[16/10] overflow-hidden bg-site-bg">
                  <Img
                    ctx={ctx}
                    image={item.image}
                    sizes="(min-width: 1024px) 360px, (min-width: 672px) 50vw, 100vw"
                    path={`content.items.${i}.image`}
                    className="transition-transform duration-700 group-hover:scale-[1.05] motion-reduce:transition-none"
                  />
                </div>
              ) : null}
              <div className="flex flex-1 flex-col p-6 @3xl:p-7">
                {!item.image ? (
                  <span aria-hidden="true" className="mb-5 grid size-11 place-items-center rounded-site-sm bg-site-primary/10 text-sm font-semibold text-site-primary tabular-nums transition-colors duration-300 group-hover:bg-site-primary group-hover:text-site-on-primary">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                ) : null}
                <h3 className="baze-heading text-xl leading-snug" {...field(ctx, `content.items.${i}.title`)}>
                  {item.title}
                </h3>
                <MultiLine ctx={ctx} text={item.description} path={`content.items.${i}.description`} className="mt-2.5 text-[0.9375rem] leading-relaxed text-site-muted" />
                {item.price !== undefined || item.priceNote || ctx.site.contact.whatsapp ? (
                  <div className="mt-auto flex items-center justify-between gap-3 pt-6">
                    <Price value={item.price} note={item.priceNote} className="text-site-text" />
                    <OrderLink ctx={ctx} message={ASK} produk={item.title} label="Tanya" className={askLink} />
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </Container>
    </SectionShell>
  );
}
